<script setup lang="ts">
import { ref } from 'vue'
import PhoneInput from '@/Components/Forms/PhoneInput.vue'
import StudentSettingsLayout from '@/Layouts/StudentSettingsLayout.vue'
import { MapPin, Plus, Pencil, Trash2, X, Check, AlertTriangle } from 'lucide-vue-next'

interface Address {
  id: number
  first_name: string
  last_name: string
  phone: string | null
  address1: string
  address2: string | null
  city: string
  zip: string
  country: string
  is_billing_default: boolean
  is_delivery_default: boolean
}

interface User {
  id: number
  name: string
  phone: string | null
}

const props = defineProps<{
  addresses: Address[]
  user: User
}>()

const addressList = ref<Address[]>([...props.addresses])
const showModal = ref(false)
const showDeleteModal = ref(false)
const addressToDelete = ref<Address | null>(null)
const isEditing = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const errors = ref<Record<string, string[]>>({})

const emptyForm = {
  id: null as number | null,
  first_name: '',
  last_name: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  zip: '',
  country: 'United Kingdom',
  is_billing_default: false,
  is_delivery_default: false,
}

const form = ref({ ...emptyForm })

const countries = [
  'United Kingdom',
  'United States',
  'Canada',
  'Australia',
  'Ireland',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
]

function openAddModal() {
  // Prefill with user's name
  const nameParts = (props.user.name || '').split(' ')
  form.value = {
    ...emptyForm,
    first_name: nameParts[0] || '',
    last_name: nameParts.slice(1).join(' ') || '',
    phone: props.user.phone || '',
  }
  isEditing.value = false
  showModal.value = true
  errors.value = {}
}

function openEditModal(address: Address) {
  form.value = {
    id: address.id,
    first_name: address.first_name,
    last_name: address.last_name,
    phone: address.phone || '',
    address1: address.address1,
    address2: address.address2 || '',
    city: address.city,
    zip: address.zip,
    country: address.country,
    is_billing_default: address.is_billing_default,
    is_delivery_default: address.is_delivery_default,
  }
  isEditing.value = true
  showModal.value = true
  errors.value = {}
}

function closeModal() {
  showModal.value = false
  form.value = { ...emptyForm }
  errors.value = {}
}

function openDeleteModal(address: Address) {
  addressToDelete.value = address
  showDeleteModal.value = true
}

function closeDeleteModal() {
  showDeleteModal.value = false
  addressToDelete.value = null
}

async function saveAddress() {
  isSaving.value = true
  errors.value = {}
  errorMessage.value = ''

  try {
    const { data } = await window.axios.post('/student/settings/addresses', form.value)

    // Update local list
    if (isEditing.value) {
      const index = addressList.value.findIndex(a => a.id === form.value.id)
      if (index !== -1) {
        addressList.value[index] = data.address
      }
    } else {
      addressList.value.push(data.address)
    }

    successMessage.value = isEditing.value ? 'Address updated successfully!' : 'Address added successfully!'
    setTimeout(() => { successMessage.value = '' }, 3000)
    closeModal()
  } catch (error: any) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else {
      errorMessage.value = error.response?.data?.message || error.message || 'An error occurred'
    }
  } finally {
    isSaving.value = false
  }
}

async function confirmDelete() {
  if (!addressToDelete.value) return

  isDeleting.value = true
  errorMessage.value = ''

  try {
    await window.axios.delete('/student/settings/addresses', {
      data: { id: addressToDelete.value.id }
    })

    addressList.value = addressList.value.filter(a => a.id !== addressToDelete.value?.id)
    successMessage.value = 'Address deleted successfully!'
    setTimeout(() => { successMessage.value = '' }, 3000)
    closeDeleteModal()
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || 'Failed to delete address'
  } finally {
    isDeleting.value = false
  }
}

function formatAddress(address: Address): string {
  const parts = [address.address1, address.city, address.zip].filter(Boolean)
  return parts.join(', ')
}
</script>

<template>
  <StudentSettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <Check class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <X class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Header with Add Button -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Saved Addresses</h2>
            <p class="text-gray-500 text-sm mt-1">Manage your billing and delivery addresses</p>
          </div>
          <button
            @click="openAddModal"
            type="button"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus class="w-4 h-4 mr-2" />
            Add Address
          </button>
        </div>

        <!-- Empty State -->
        <div v-if="addressList.length === 0" class="text-center py-12">
          <MapPin class="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">No addresses yet</h3>
          <p class="text-gray-500">Add an address to get started</p>
        </div>

        <!-- Address List -->
        <div v-else class="space-y-4">
          <div
            v-for="address in addressList"
            :key="address.id"
            class="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
          >
            <div class="flex items-start gap-3">
              <MapPin class="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium text-gray-900">{{ address.first_name }} {{ address.last_name }}</span>
                </div>
                <p class="text-sm text-gray-600">{{ formatAddress(address) }}</p>
                <p v-if="address.phone" class="text-sm text-gray-500 mt-1">{{ address.phone }}</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button
                @click="openEditModal(address)"
                type="button"
                class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit"
              >
                <Pencil class="w-4 h-4" />
              </button>
              <button
                @click="openDeleteModal(address)"
                type="button"
                class="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Add/Edit Address Modal -->
    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showModal"
          class="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <!-- Background overlay -->
            <div
              class="fixed inset-0 bg-gray-500/75 transition-opacity"
              @click="closeModal"
            ></div>

            <!-- Modal panel -->
            <div class="relative inline-block bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
              <div class="bg-white px-6 pt-6 pb-4">
                <div class="flex items-center justify-between mb-6">
                  <h3 class="text-lg font-semibold text-gray-900">
                    {{ isEditing ? 'Edit Address' : 'Add New Address' }}
                  </h3>
                  <button
                    @click="closeModal"
                    type="button"
                    class="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X class="w-5 h-5" />
                  </button>
                </div>

                <form @submit.prevent="saveAddress" class="space-y-4">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        v-model="form.first_name"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p v-if="errors.first_name" class="mt-1 text-xs text-red-600">{{ errors.first_name[0] }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        v-model="form.last_name"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p v-if="errors.last_name" class="mt-1 text-xs text-red-600">{{ errors.last_name[0] }}</p>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                    <PhoneInput v-model="form.phone" />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                    <input
                      v-model="form.address1"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="123 Main Street"
                    />
                    <p v-if="errors.address1" class="mt-1 text-xs text-red-600">{{ errors.address1[0] }}</p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Address Line 2 (Optional)</label>
                    <input
                      v-model="form.address2"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Apartment, suite, etc."
                    />
                  </div>

                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        v-model="form.city"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p v-if="errors.city" class="mt-1 text-xs text-red-600">{{ errors.city[0] }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <input
                        v-model="form.zip"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p v-if="errors.zip" class="mt-1 text-xs text-red-600">{{ errors.zip[0] }}</p>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select
                      v-model="form.country"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option v-for="country in countries" :key="country" :value="country">{{ country }}</option>
                    </select>
                  </div>
                </form>
              </div>

              <div class="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  @click="closeModal"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  @click="saveAddress"
                  :disabled="isSaving"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  <svg v-if="isSaving" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isSaving ? 'Saving...' : 'Save Address' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showDeleteModal"
          class="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="delete-modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <!-- Background overlay -->
            <div
              class="fixed inset-0 bg-gray-500/75 transition-opacity"
              @click="closeDeleteModal"
            ></div>

            <!-- Modal panel -->
            <div class="relative inline-block bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-md sm:w-full">
              <div class="bg-white px-6 pt-6 pb-4">
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle class="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900" id="delete-modal-title">
                      Delete Address
                    </h3>
                    <p class="mt-2 text-sm text-gray-500">
                      Are you sure you want to delete this address? This action cannot be undone.
                    </p>
                    <div v-if="addressToDelete" class="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p class="text-sm font-medium text-gray-900">
                        {{ addressToDelete.first_name }} {{ addressToDelete.last_name }}
                      </p>
                      <p class="text-sm text-gray-600">{{ formatAddress(addressToDelete) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  @click="closeDeleteModal"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  @click="confirmDelete"
                  :disabled="isDeleting"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  <svg v-if="isDeleting" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isDeleting ? 'Deleting...' : 'Delete Address' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </StudentSettingsLayout>
</template>
